import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createShipment, createContainersBulk, getClients, getImporters, getSuppliers, getCatalog, createClient, createImporter, createSupplier, createCatalogItem, deleteCatalogItem } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Plus, Package, Ship, Calendar, Building2, Truck, DollarSign, Anchor, Trash2 } from 'lucide-react';

export default function NewShipmentPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [importers, setImporters] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [shipmentStatuses, setShipmentStatuses] = useState([]);
  const [puertos, setPuertos] = useState([]);
  
  // Quick add dialogs
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddImporter, setShowAddImporter] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showAddPuerto, setShowAddPuerto] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newImporterName, setNewImporterName] = useState('');
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newPuertoName, setNewPuertoName] = useState('');
  
  const [form, setForm] = useState({
    mbl: '',
    client_id: '',
    origin: '',
    destination: '',
    puerto: '',
    incoterm_code: '',
    vessel_name: '',
    etd_final_destination: '',
    eta_port: '',
    eta_final: '',
    status_general_code: 'NO_SALIDO_CHINA',
    importer_id: '',
    supplier_id: '',
    arnulfo_applies: false,
    arnulfo_status: 'NO_APLICA',
    cost_calculated: false,
    total_cost: '',
  });
  
  // Containers to add with shipment
  const [containersText, setContainersText] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, importersRes, suppliersRes, incotermsRes, statusesRes, puertosRes] = await Promise.all([
        getClients(),
        getImporters(),
        getSuppliers(),
        getCatalog('incoterms'),
        getCatalog('shipment_status'),
        getCatalog('puertos'),
      ]);
      setClients(clientsRes.data);
      setImporters(importersRes.data);
      setSuppliers(suppliersRes.data);
      setIncoterms(incotermsRes.data);
      setShipmentStatuses(statusesRes.data);
      setPuertos(puertosRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const handleQuickAddClient = async () => {
    if (!newClientName.trim()) return;
    try {
      const res = await createClient({ name: newClientName.trim() });
      setClients([...clients, res.data]);
      setForm({ ...form, client_id: res.data.id });
      setNewClientName('');
      setShowAddClient(false);
      toast.success(t('shipments.clientCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingClient'));
    }
  };

  const handleQuickAddImporter = async () => {
    if (!newImporterName.trim()) return;
    try {
      const res = await createImporter({ name: newImporterName.trim() });
      setImporters([...importers, res.data]);
      setForm({ ...form, importer_id: res.data.id });
      setNewImporterName('');
      setShowAddImporter(false);
      toast.success(t('shipments.importerCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingImporter'));
    }
  };

  const handleQuickAddSupplier = async () => {
    if (!newSupplierName.trim()) return;
    try {
      const res = await createSupplier({ name: newSupplierName.trim() });
      setSuppliers([...suppliers, res.data]);
      setForm({ ...form, supplier_id: res.data.id });
      setNewSupplierName('');
      setShowAddSupplier(false);
      toast.success(t('shipments.supplierCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingSupplier'));
    }
  };

  const handleDeletePuerto = async (code) => {
    try {
      await deleteCatalogItem('puertos', code);
      setPuertos(puertos.filter(p => p.code !== code));
      toast.success('Puerto eliminado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar el puerto');
    }
  };

  const handleQuickAddPuerto = async () => {
    if (!newPuertoName.trim()) return;
    try {
      const code = newPuertoName.trim().toUpperCase().replace(/\s+/g, '_').normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const res = await createCatalogItem('puertos', { 
        code, 
        label: newPuertoName.trim(), 
        sort_order: puertos.length + 1, 
        is_active: true 
      });
      setPuertos([...puertos, res.data]);
      setForm({ ...form, puerto: res.data.label });
      setNewPuertoName('');
      setShowAddPuerto(false);
      toast.success(t('shipments.portCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingPort'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.mbl.trim()) {
      toast.error(t('shipments.mblRequired'));
      return;
    }
    if (!form.client_id) {
      toast.error(t('shipments.clientRequired'));
      return;
    }

    setSaving(true);
    try {
      const shipmentData = {
        ...form,
        mbl: form.mbl.toUpperCase(),
        importer_id: form.importer_id === 'none' || !form.importer_id ? null : form.importer_id,
        supplier_id: form.supplier_id === 'none' || !form.supplier_id ? null : form.supplier_id,
        total_cost: form.total_cost ? parseFloat(form.total_cost) : null,
      };
      
      await createShipment(shipmentData);
      
      // Add containers if provided
      const containerLines = containersText.split(/[\n\s,]+/).filter(c => c.trim());
      if (containerLines.length > 0) {
        const containersToAdd = containerLines.map(num => ({
          container_number: num.trim().toUpperCase(),
          status_container_code: 'NO_ARRIBADO',
          release_traffic_light: 'NA',
        }));
        await createContainersBulk(form.mbl.toUpperCase(), containersToAdd);
      }
      
      toast.success(t('shipments.shipmentCreated'));
      navigate(`/shipments/${form.mbl.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingShipment'));
    } finally {
      setSaving(false);
    }
  };

  // Group statuses for better UX (similar to Notion)
  const statusGroups = {
    pending: shipmentStatuses.filter(s => s.group === 'pending' || s.code.includes('NO_SALIDO')),
    in_progress: shipmentStatuses.filter(s => s.group === 'in_progress' || ['EN_TRANSITO', 'EN_PUERTO', 'PARADO', 'VERDE', 'EN_DESTINO'].some(k => s.code.includes(k))),
    completed: shipmentStatuses.filter(s => s.group === 'completed' || s.code.includes('ENTREGADO')),
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl" data-testid="new-shipment-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/shipments')} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-3xl">Nuevo Embarque</h1>
            <p className="text-muted-foreground">Crear un nuevo registro de embarque (formato Notion)</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* MBL & Client - Main identification */}
          <div className="bg-card border border-border rounded-sm p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Ship className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg">Identificación del Embarque</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mbl">MBL/BL *</Label>
                <Input
                  id="mbl"
                  value={form.mbl}
                  onChange={(e) => setForm({...form, mbl: e.target.value.toUpperCase()})}
                  placeholder="Ej: MAEU123456789"
                  className="mt-1 font-mono text-lg"
                  required
                  data-testid="mbl-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Número de conocimiento de embarque</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Cliente *</Label>
                  <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
                    <DialogTrigger asChild>
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs">
                        <Plus size={12} className="mr-1" /> Nuevo
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Agregar Cliente</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Nombre del Cliente</Label>
                          <Input
                            value={newClientName}
                            onChange={(e) => setNewClientName(e.target.value)}
                            placeholder="Ej: LIN, FX, ISIMART"
                            className="mt-1"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddClient(false)}>Cancelar</Button>
                          <Button onClick={handleQuickAddClient}>Crear Cliente</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <Select 
                  value={form.client_id} 
                  onValueChange={(v) => setForm({...form, client_id: v})}
                  required
                >
                  <SelectTrigger className="mt-1" data-testid="client-select">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Status - prominent like in Notion */}
            <div>
              <Label>Estatus General</Label>
              <Select 
                value={form.status_general_code} 
                onValueChange={(v) => setForm({...form, status_general_code: v})}
              >
                <SelectTrigger className="mt-1 w-full md:w-80" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusGroups.pending?.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground bg-secondary/50">{t('shipments.pending')}</div>
                      {statusGroups.pending.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                      ))}
                    </>
                  )}
                  {statusGroups.in_progress?.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground bg-secondary/50 mt-1">{t('shipments.inProgress')}</div>
                      {statusGroups.in_progress.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                      ))}
                    </>
                  )}
                  {statusGroups.completed?.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground bg-secondary/50 mt-1">{t('shipments.completed')}</div>
                      {statusGroups.completed.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Containers - Like Notion's multi-line field */}
          <div className="bg-card border border-border rounded-sm p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg">{t('shipments.containers')}</h3>
            </div>
            
            <div>
              <Label>{t('shipments.containerNumbers')}</Label>
              <Textarea
                value={containersText}
                onChange={(e) => setContainersText(e.target.value)}
                placeholder="Ingresa los números de contenedor separados por espacio, coma o línea:&#10;MSCU1234567 TCLU7654321&#10;CMAU9876543"
                rows={3}
                className="mt-1 font-mono"
                data-testid="containers-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Puedes agregar múltiples contenedores separados por espacio, coma o salto de línea (como en Notion)
              </p>
            </div>
          </div>

          {/* Route & Dates */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Route */}
            <div className="bg-card border border-border rounded-sm p-5 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg">{t('shipments.route')}</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>{t('shipments.origin')}</Label>
                  <Input
                    value={form.origin}
                    onChange={(e) => setForm({...form, origin: e.target.value})}
                    placeholder="Ej: Shanghai, China"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Destino Final</Label>
                  <Input
                    value={form.destination}
                    onChange={(e) => setForm({...form, destination: e.target.value})}
                    placeholder="Ej: CDMX, Monterrey, Guadalajara"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Puerto de Arribo</Label>
                  <div className="flex gap-2 mt-1">
                    <Select 
                      value={form.puerto} 
                      onValueChange={(v) => setForm({...form, puerto: v})}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Seleccionar puerto" />
                      </SelectTrigger>
                      <SelectContent>
                        {puertos.map(p => (
                          <SelectItem key={p.code} value={p.label}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Dialog open={showAddPuerto} onOpenChange={setShowAddPuerto}>
                      <DialogTrigger asChild>
                        <Button type="button" variant="outline" size="icon" title="Agregar nuevo puerto">
                          <Plus size={16} />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Anchor size={20} />
                            Agregar Puerto
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          {puertos.length > 0 && (
                            <div className="border border-border rounded-md divide-y divide-border max-h-40 overflow-y-auto" data-testid="puerto-list">
                              {puertos.map(p => (
                                <div key={p.code} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`puerto-row-${p.code}`}>
                                  <span>{p.label}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleDeletePuerto(p.code)}
                                    className="text-rose-500 hover:text-rose-400 transition-colors"
                                    title="Eliminar puerto"
                                    data-testid={`delete-puerto-${p.code}`}
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div>
                            <Label>Nombre del Puerto</Label>
                            <Input
                              value={newPuertoName}
                              onChange={(e) => setNewPuertoName(e.target.value)}
                              placeholder="Ej: Manzanillo"
                              className="mt-1"
                              onKeyDown={(e) => e.key === 'Enter' && handleQuickAddPuerto()}
                            />
                          </div>
                          <Button onClick={handleQuickAddPuerto} className="w-full">
                            Agregar Puerto
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div>
                  <Label>Incoterm</Label>
                  <Select 
                    value={form.incoterm_code} 
                    onValueChange={(v) => setForm({...form, incoterm_code: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {incoterms.map(i => (
                        <SelectItem key={i.code} value={i.code}>{i.code} - {i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Buque</Label>
                  <Input
                    value={form.vessel_name}
                    onChange={(e) => setForm({...form, vessel_name: e.target.value})}
                    placeholder="Nombre del buque"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-card border border-border rounded-sm p-5 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg">{t('shipments.dates')}</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>ETD Origen (Salida)</Label>
                  <Input
                    type="date"
                    value={form.etd_final_destination}
                    onChange={(e) => setForm({...form, etd_final_destination: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>ETA Puerto (ETA Inicial)</Label>
                  <Input
                    type="date"
                    value={form.eta_port}
                    onChange={(e) => setForm({...form, eta_port: e.target.value})}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Fecha estimada de arribo al puerto</p>
                </div>
                <div>
                  <Label>ETA Final (Entrega Estimada)</Label>
                  <Input
                    type="date"
                    value={form.eta_final}
                    onChange={(e) => setForm({...form, eta_final: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Importer / Supplier */}
          <div className="bg-card border border-border rounded-sm p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg">Importadora / Proveedor</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t('common.status') === 'Estatus' ? 'Importadora' : 'Importer'}</Label>
                  <Dialog open={showAddImporter} onOpenChange={setShowAddImporter}>
                    <DialogTrigger asChild>
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs">
                        <Plus size={12} className="mr-1" /> {t('common.new')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('common.status') === 'Estatus' ? 'Agregar Importadora' : 'Add Importer'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>{t('common.status') === 'Estatus' ? 'Nombre de la Importadora' : 'Importer Name'}</Label>
                          <Input
                            value={newImporterName}
                            onChange={(e) => setNewImporterName(e.target.value)}
                            placeholder={t('common.status') === 'Estatus' ? 'Nombre de la importadora' : 'Importer name'}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddImporter(false)}>{t('common.cancel')}</Button>
                          <Button onClick={handleQuickAddImporter}>{t('common.create')}</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <Select 
                  value={form.importer_id} 
                  onValueChange={(v) => setForm({...form, importer_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('common.unassigned')}</SelectItem>
                    {importers.map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t('common.status') === 'Estatus' ? 'Proveedor' : 'Supplier'}</Label>
                  <Dialog open={showAddSupplier} onOpenChange={setShowAddSupplier}>
                    <DialogTrigger asChild>
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs">
                        <Plus size={12} className="mr-1" /> {t('common.new')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('common.status') === 'Estatus' ? 'Agregar Proveedor' : 'Add Supplier'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>{t('common.status') === 'Estatus' ? 'Nombre del Proveedor' : 'Supplier Name'}</Label>
                          <Input
                            value={newSupplierName}
                            onChange={(e) => setNewSupplierName(e.target.value)}
                            placeholder={t('common.status') === 'Estatus' ? 'Nombre del proveedor' : 'Supplier name'}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddSupplier(false)}>{t('common.cancel')}</Button>
                          <Button onClick={handleQuickAddSupplier}>{t('common.create')}</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <Select 
                  value={form.supplier_id} 
                  onValueChange={(v) => setForm({...form, supplier_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Cost & Internal Fields - Like Notion */}
          <div className="bg-card border border-border rounded-sm p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg">Costo y Campos Internos</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.cost_calculated}
                    onCheckedChange={(v) => setForm({...form, cost_calculated: v})}
                    id="cost-calculated"
                  />
                  <Label htmlFor="cost-calculated">Costo Calculado</Label>
                </div>
                
                {form.cost_calculated && (
                  <div>
                    <Label>Costo Total (USD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.total_cost}
                      onChange={(e) => setForm({...form, total_cost: e.target.value})}
                      placeholder="0.00"
                      className="mt-1 font-mono"
                    />
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.arnulfo_applies}
                    onCheckedChange={(v) => setForm({...form, arnulfo_applies: v, arnulfo_status: v ? 'PENDIENTE' : 'NO_APLICA'})}
                    id="arnulfo-applies"
                  />
                  <Label htmlFor="arnulfo-applies">Acuerdo de Pago (Aplica Arnulfo)</Label>
                </div>
                
                {form.arnulfo_applies && (
                  <div>
                    <Label>Estatus Acuerdo</Label>
                    <Select 
                      value={form.arnulfo_status} 
                      onValueChange={(v) => setForm({...form, arnulfo_status: v})}
                    >
                      <SelectTrigger className="mt-1 w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                        <SelectItem value="PAGADO">Pagado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/shipments')}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} data-testid="create-shipment-btn" size="lg">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Crear Embarque
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
